/**
 * Client mirror of backend `_fare_alert_matches_acceptance` (tripjack_booking.py).
 * Used to avoid re-opening FareRepriceModal when the supplier echoes the same FAREALERT.
 */

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
}

function parseAcceptanceTotals(acceptance) {
  if (!acceptance || typeof acceptance !== 'object') return null;
  const oldAcc = num(acceptance.old_total);
  const newAcc = num(acceptance.new_total);
  let deltaAcc = num(acceptance.delta);
  if (!Number.isFinite(deltaAcc) && Number.isFinite(oldAcc) && Number.isFinite(newAcc)) {
    deltaAcc = newAcc - oldAcc;
  }
  if (!Number.isFinite(oldAcc) || !Number.isFinite(newAcc) || !Number.isFinite(deltaAcc)) {
    return null;
  }
  return { oldAcc, newAcc, deltaAcc };
}

function tripleOk(summary, { oldAcc, newAcc, deltaAcc }, tolerance) {
  if (!summary || typeof summary !== 'object') return false;
  const fareOld = num(summary.old_fare);
  const fareNew = num(summary.new_fare);
  let fareDelta = num(summary.delta);
  if (!Number.isFinite(fareDelta) && Number.isFinite(fareOld) && Number.isFinite(fareNew)) {
    fareDelta = fareNew - fareOld;
  }
  if (!Number.isFinite(fareOld) || !Number.isFinite(fareNew) || !Number.isFinite(fareDelta)) {
    return false;
  }
  return (
    Math.abs(fareOld - oldAcc) <= tolerance
    && Math.abs(fareNew - newAcc) <= tolerance
    && Math.abs(fareDelta - deltaAcc) <= tolerance
  );
}

/**
 * @param {Record<string, unknown>|null|undefined} alertSummary - fare_alert_summary shape
 * @param {Record<string, unknown>|null|undefined} acceptance - { old_total, new_total, delta }
 * @param {number} [tolerance=1]
 */
export function fareAlertMatchesAcceptance(alertSummary, acceptance, tolerance = 1) {
  const parsed = parseAcceptanceTotals(acceptance);
  if (!parsed || !alertSummary) return false;
  return tripleOk(alertSummary, parsed, tolerance);
}

/**
 * Extract supplier TF from Tripjack /review-shaped body.
 * @param {Record<string, unknown>|null|undefined} reviewData
 * @returns {number|null}
 */
export function extractSupplierTfFromReview(reviewData) {
  if (!reviewData || typeof reviewData !== 'object') return null;
  const tpi = reviewData.totalPriceInfo;
  if (!tpi || typeof tpi !== 'object') return null;
  const tfd = tpi.totalFareDetail || tpi.totalFareDetail;
  const fc = (tfd && typeof tfd === 'object' ? tfd.fC : null) || {};
  const tf = num(fc.TF);
  return Number.isFinite(tf) && tf > 0 ? tf : null;
}

export function roundFareTotal(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100) / 100;
}

/**
 * Resolve accepted new total from modal success payload.
 * Preserves markup delta when supplier TF changes (fixed per-pax markup).
 */
export function resolveAcceptedNewTotal(payload, acceptance, options = {}) {
  const { previousMarkedUpTotal } = options;
  const fromAccept = roundFareTotal(acceptance?.new_total);
  if (fromAccept != null) return fromAccept;
  const fromSummary = roundFareTotal(payload?.fare_alert_summary?.new_fare);
  if (fromSummary != null) return fromSummary;
  const newSupplierTf = extractSupplierTfFromReview(payload?.data);
  if (newSupplierTf != null) {
    const oldSupplierTf =
      roundFareTotal(acceptance?.old_total)
      ?? roundFareTotal(payload?.fare_alert_summary?.old_fare);
    if (
      oldSupplierTf != null
      && previousMarkedUpTotal != null
      && Number.isFinite(previousMarkedUpTotal)
      && previousMarkedUpTotal > oldSupplierTf + 0.5
    ) {
      const markupDelta = previousMarkedUpTotal - oldSupplierTf;
      return roundFareTotal(newSupplierTf + markupDelta);
    }
    return roundFareTotal(newSupplierTf);
  }
  return null;
}
